import {openConfirmDialog, showNotification} from 'platform/components';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {
  UpdateVehicleOnPlatformApiArg,
  useLazyGetSalesPlatformsQuery,
  useUpdateVehicleOnAllPlatformsMutation,
  useUpdateVehicleOnPlatformMutation,
} from '@dms/api/sales';
import {useLazyGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {handleApiError} from '../utils/handleApiError';

export function useAdPlatformsVehicleUpdateDialog() {
  const [getSalesPlatforms] = useLazyGetSalesPlatformsQuery();
  const [getSaleVehicle] = useLazyGetSaleVehicleQuery();
  const [updateVehicleOnAllPlatforms] = useUpdateVehicleOnAllPlatformsMutation();
  const [updateVehicleOnSinglePlatform] = useUpdateVehicleOnPlatformMutation();

  async function update(
    vehicleId: string,
    platformCode?: UpdateVehicleOnPlatformApiArg['platformCode'],
    onClose?: () => void
  ): Promise<void | false> {
    const saleVehicle = await getSaleVehicle({vehicleId});

    if (isNotNil(saleVehicle.error)) {
      return false;
    }

    const hasAdvertisements = await getSalesPlatforms({
      vehicleId,
    })
      .unwrap()
      .then((advertisements) =>
        advertisements.some((ad) => ad.enabled && ad.status === 'published')
      )
      .catch(handleApiError);

    if (!hasAdvertisements) {
      return false;
    }

    openConfirmDialog({
      isInLightbox: true,
      text: i18n.t('entity.vehicle.labels.infoChangedUpdateClassifiedsQuestion'),
      onConfirm: () => {
        if (isNil(platformCode) || isEmpty(platformCode)) {
          return updateVehicleOnAllPlatforms({
            vehicleId,
          })
            .unwrap()
            .then(() => {
              showNotification.success(i18n.t('entity.vehicle.labels.classifiedsWillBeUpdated'));
            })
            .catch(handleApiError);
        }

        return updateVehicleOnSinglePlatform({
          vehicleId,
          platformCode,
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('entity.vehicle.labels.classifiedsWillBeUpdated'));
          })
          .catch(handleApiError);
      },
      onCloseComplete: () => {
        if (onClose) {
          onClose();
        }
      },
    });
  }

  return [update];
}
